import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SliceCaseReducers } from '@reduxjs/toolkit/src/createSlice';
import { EnumErrorType } from '../../types/ErrorsObject';
import { AppDispatch, RootState } from '../store';
import NewRelicAgent from '../../newrelic';
import { CHECK_EXPIRED_MESSAGE, HttpStatus } from '../../constants/http';
import { changeStep } from './step.slice';
import axios, { AxiosError } from 'axios';

export interface ErrorInformation {
	type: EnumErrorType;
	error: object;
	message: string;
}

type State = ErrorInformation | {};

const initialState = {};

const errorsSlice = createSlice<State, SliceCaseReducers<State>>({
	name: 'errorsSlice',
	initialState,
	reducers: {
		setError(state, action: PayloadAction<ErrorActionPayload>) {
			const { type, error } = action.payload;
			const errorMessage = error.message;
			return {
				type,
				error,
				message: errorMessage
			};
		}
	}
});

type ErrorActionPayload = { type: EnumErrorType; error: Error };

export const errorsActions = errorsSlice.actions;

export default errorsSlice;

export const selectError = (state: RootState) => state.error;
export const selectErrorType = (state: RootState) => {
	if (!('type' in state.error)) return;
	return state.error.type;
};

export const selectMaintenanceModeDetails = (state: RootState) => {
	return (state as { error: { error: AxiosError } }).error.error.response?.data;
};

export const handleError = (error: any, type?: EnumErrorType) => {
	return (dispatch: AppDispatch) => {
		NewRelicAgent.noticeError(error, { message: error.message, stack: error.stack });
		const errorType = getErrorType(error) || type || EnumErrorType.APP_ERROR;
		dispatch(
			errorsActions.setError({
				type: errorType,
				error
			})
		);

		error.status === HttpStatus.SERVICE_UNAVAILABLE
			? dispatch(changeStep('MAINTENANCE_MODE_STEP'))
			: dispatch(changeStep('ERROR_STEP'));
	};
};

function getErrorType(error: any): EnumErrorType | undefined {
	if (isGatewayResponseError(error)) {
		return EnumErrorType.LOAD_ISSUES_ERROR;
	}
	if (isExpiredTokenError(error)) {
		return EnumErrorType.CHECK_EXPIRED;
	}
	return undefined;
}

export const isGatewayResponseError = (error: any): boolean => {
	const status = error.response?.status;
	return (
		status === HttpStatus.BAD_GATEWAY ||
		status === HttpStatus.SERVICE_UNAVAILABLE ||
		status === HttpStatus.GATEWAY_TIMEOUT
	);
};
export const isExpiredTokenError = (error: any): boolean => {
	if (axios.isAxiosError(error) && error.response?.status === 401) {
		const body = error.response?.data;
		return body?.type === CHECK_EXPIRED_MESSAGE || false;
	}
	return false;
};
